import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Wizard from '../components/wizard/Wizard';
import StepClient from '../components/wizard/StepClient';
import StepVendor from '../components/wizard/StepVendor';
import StepDetails from '../components/wizard/StepDetails';
import StepCompletion from '../components/wizard/StepCompletion';
import { WorkOrderWizardData } from '../types';

const CreateWorkOrderPage: React.FC = () => {
	const queryClient = useQueryClient();
	const location = useLocation();
	const prefilledWizardData = (location.state as { prefilledWizardData?: WorkOrderWizardData } | null)?.prefilledWizardData;
	const fromEmailImport = !!prefilledWizardData?.fromEmailImport;
	const importInitialStep = fromEmailImport
		&& prefilledWizardData?.selectedClient
		&& prefilledWizardData?.selectedClientLocationId
		&& prefilledWizardData?.priorityId
		&& prefilledWizardData?.statusId
		&& prefilledWizardData?.categoryId
		? 3
		: 0;
	const [wizardData] = useState<WorkOrderWizardData>(() => ({
		selectedVendors: [],
		radius: 50,
		fromEmailImport,
		...prefilledWizardData,
	}));
	const [isWorkOrderCreated, setIsWorkOrderCreated] = useState(false);

	const handleWorkOrderCreated = () => {
		setIsWorkOrderCreated(true);
		// Invalidate work orders queries to refresh the list
		queryClient.invalidateQueries({ queryKey: ['workorders-v2'] });
		queryClient.invalidateQueries({ queryKey: ['workOrdersV2'] });
		queryClient.invalidateQueries({ queryKey: ['dashboard-workorders'] });
		queryClient.invalidateQueries({ queryKey: ['workorders'] });
	};

	const handleWizardComplete = async (data: WorkOrderWizardData) => {
		try {
			console.log('Work Order Wizard Data:', data);
			// The StepCompletion component handles the actual work order creation
			// This is just a fallback if somehow the wizard completes without going through StepCompletion
			toast.success('Work order wizard completed!');
		} catch (error) {
			console.error('Error completing wizard:', error);
			toast.error('Failed to complete wizard');
		}
	};

	// const handleWizardDataChange = (newData: Partial<WorkOrderWizardData>) => {
	// 	console.log('Wizard data changed:', newData);
	// 	setWizardData(prev => ({ ...prev, ...newData }));
	// }; // Unused

	const handleWizardCancel = () => {
		if (fromEmailImport) {
			window.location.assign('/workorders');
			return;
		}
		window.history.back();
	};

	return (
		<div>
			<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Create Work Order</h1>
			<Wizard
				steps={[
					{ id: 'client', title: 'Client Selection', description: 'Choose client and search parameters', component: StepClient },
					{ id: 'vendor', title: 'Vendor Selection', description: 'Select vendor based on distance and categories (optional)', component: StepVendor },
					{ id: 'details', title: 'Work Details', description: 'Enter work order details and assign user', component: StepDetails },
					{ id: 'completion', title: 'Complete', description: 'Review and create work order', component: StepCompletion },
				]}
				onComplete={handleWizardComplete}
				onCancel={handleWizardCancel}
				initialData={wizardData}
				initialStep={importInitialStep}
				suppressLeaveWarning={fromEmailImport && !isWorkOrderCreated}
				isWorkOrderCreated={isWorkOrderCreated}
				onWorkOrderCreated={handleWorkOrderCreated}
			/>
		</div>
	);
};

export default CreateWorkOrderPage;
